import { keyframes } from "glamor";
import * as React from "react";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { Or } from "../../../domain/Or";
import { TRenderable } from "../SharedTypes";
import { Style } from "../Styles/CSS";

export function FadeInArray<TItem>({
    label,
    array,
    render,
    itemDelayMs,
    displayInline,
    limitAnimationToItems,
    containerClass,
}: {
    label: string;
    array: Array<TItem>;
    render: (item: TItem, index?: number) => TRenderable;
    itemDelayMs?: number;
    displayInline?: boolean;
    limitAnimationToItems?: number;
    containerClass?: string;
}) {
    if (array === undefined) {
        return null;
    }

    if (IsMobileDevice()) {
        return (
            <span className={containerClass}>
                {array.map((item, index) =>
                    displayInline ? (
                        <span key={index}>{render(item, index)}</span>
                    ) : (
                        <div key={index}>{render(item, index)}</div>
                    )
                )}
            </span>
        );
    }

    return (
        <span className={containerClass}>
            {array.map((item, index) => (
                <FadeInElement
                    index={index}
                    key={index}
                    uniqueId={`${label}_${index}`}
                    itemDelayMs={itemDelayMs}
                    displayInline={displayInline}
                    limitAnimationtoItems={limitAnimationToItems}
                >
                    {render(item, index)}
                </FadeInElement>
            ))}
        </span>
    );
}

export function FadeInText({
    text,
    characterDelayMs,
    withClass,
    tooltip,
}: {
    text: string;
    characterDelayMs?: number;
    withClass?: string;
    tooltip?: string;
}) {
    if (text === "") {
        return <span />;
    }

    if (IsMobileDevice()) {
        return (
            <span data-tip={tooltip} className={withClass}>
                {text}
            </span>
        );
    }

    return (
        <span data-tip={tooltip} className={withClass} key={text}>
            {text.split("").map((character, index) => (
                <FadeInElement
                    index={index}
                    uniqueId={text}
                    key={index}
                    itemDelayMs={characterDelayMs}
                    displayInline={true}
                >
                    {character}
                </FadeInElement>
            ))}
        </span>
    );
}

function FadeInElement({
    index,
    children,
    uniqueId,
    itemDelayMs,
    displayInline,
    limitAnimationtoItems,
}: {
    index: number;
    children: TRenderable;
    uniqueId: string;
    itemDelayMs?: number;
    displayInline?: boolean;
    limitAnimationtoItems?: number;
}) {
    const animationStyle = GetAnimationStyle(
        index,
        uniqueId,
        itemDelayMs,
        limitAnimationtoItems
    );

    return displayInline ? (
        <span {...animationStyle}>{children}</span>
    ) : (
        <div {...animationStyle}>{children}</div>
    );
}

function GetAnimationStyle(
    index: number,
    uniqueId: string,
    itemDelayMs?: number,
    limitAnimationToItems?: number
) {
    const animationStyle = Style(`FadeIn_${uniqueId}`, {
        animationName: keyframes({
            "0%": { opacity: 0 },
            "100%": { opacity: 1 },
        }),
        animationDuration: `1s`,
        animationDelay: `${(Or(itemDelayMs, 200) / 1000) * index}s`,
        animationFillMode: `forwards`,
        opacity: 0,
    });

    if (!limitAnimationToItems) {
        return animationStyle;
    } else {
        if (index < limitAnimationToItems) {
            return animationStyle;
        } else {
            return {};
        }
    }
}
